import { Box, CircularProgress, List, ListItem } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../../../../components/ui/Button";
import Text from "../../../../components/ui/Text";
import { useToast } from "../../../../contexts/ToastContext";
import apiService from "../../../../services/api";

export const Receipts = () => {
  const { showToast } = useToast();
  // const [searchQuery, setSearchQuery] = useState("");

  const observerRef = useRef(null);

  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    has_next_page: false,
  });
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch transactions
  const fetchTransactions = async (params, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const response = await apiService.fetchUserTransactions({
        ...params,
        ...pagination,
      });

      if (response.success) {
        if (append) {
          setTransactions((prev) => [...prev, ...response.data]);
        } else {
          setTransactions(response.data);
        }
        setPagination(response.pagination);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const loadMoreTransactions = () => {
    if (pagination.has_next_page && !loadingMore) {
      fetchTransactions(
        { page: pagination.page + 1, limit: pagination.limit },
        true,
      );
    }
  };

  // Initial load
  useEffect(() => {
    fetchTransactions();
  }, []);

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && pagination.has_next_page && !loadingMore) {
          loadMoreTransactions();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [pagination.has_next_page, loadingMore, loadMoreTransactions]);

  return (
    <Box>
      <Text
        component="p"
        variant="paragraph"
        sx={{ mb: 2, fontSize: "16px", fontWeight: 700 }}
      >
        Receipts
      </Text>

      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}

      {!isLoading && transactions.length === 0 && (
        <Text
          component="p"
          variant="paragraph2"
          sx={{ color: "#666", my: 2, textAlign: "center", fontSize: "16px" }}
        >
          You have no receipts yet.
        </Text>
      )}

      {/* Search Input */}
      {/* <Input
        placeholder="Search for a receipt by venue name"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ paddingLeft: "12px" }}>
              <IconSearch size={16} color="#666" />
            </InputAdornment>
          ),
        }}
      /> */}

      {/* {isLoading && <LoadingSpinner />} */}

      {/* Receipts List */}
      <List
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          mt: "16px",
          mb: "32px",
        }}
      >
        {transactions.map((receipt) => (
          <ListItem
            key={receipt.id}
            sx={{
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              p: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: "8px",
              }}
            >
              <Box>
                <Text variant="subtitle2" sx={{ mb: "4px", fontSize: "16px" }}>
                  {receipt.game.venue || "N/A"}
                </Text>
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Text
                    variant="paragraph3"
                    sx={{ color: "#666", fontSize: "14px" }}
                  >
                    {receipt?.payment_completed_at
                      ? new Date(
                          receipt.payment_completed_at,
                        ).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "Unknown Date"}
                  </Text>
                  <Box
                    sx={{
                      height: "8px",
                      width: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#D4D4D4",
                    }}
                  />
                  <Text
                    variant="paragraph3"
                    sx={{ color: "#666", fontSize: "14px" }}
                  >
                    Paid €{receipt.amount.toFixed(2)}
                  </Text>
                </Box>
              </Box>
              {/* <StatusChip variant={receipt.statusVariant} label="N/A" /> */}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                justifyContent: "space-between",
              }}
            >
              <Button
                component={Link}
                variant="link"
                target="_blank"
                rel="noopener noreferrer"
                to={receipt.receipt_url}
                sx={{
                  color: "#1890FF",
                  fontSize: "14px",
                  p: 0,
                  minWidth: "unset",
                }}
              >
                View receipt
              </Button>
              <Button
                component={Link}
                variant="link"
                target="_blank"
                rel="noopener noreferrer"
                to={`/game-details/${receipt.game_id}`}
                sx={{ color: "#1890FF", fontSize: "14px", p: 0 }}
              >
                View game
              </Button>
            </Box>
          </ListItem>
        ))}
        {pagination.has_next_page && (
          <Box
            ref={observerRef}
            id="load-more-anchor"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 3,
            }}
          >
            {loadingMore ? (
              <CircularProgress size={32} />
            ) : (
              <Box sx={{ opacity: 0, height: "1px" }}>Anchor</Box>
            )}
          </Box>
        )}
      </List>
    </Box>
  );
};
